/**
 * Axios client with auth header + envelope unwrapping.
 *
 * All backend responses are `{code, msg, data}`. This client throws a
 * typed error if `code !== 0`, so route handlers can use `.then(data)`
 * without worrying about the envelope.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth';

export class ApiError extends Error {
  constructor(
    public code: number,
    message: string,
    public fields?: Record<string, string>,
    public errorKey?: string,
  ) {
    super(message);
  }
}

/** Same Vercel origin: VITE is unset/empty; external API: full https origin (no trailing slash). */
function apiBaseURL(): string {
  const u = import.meta.env.VITE_API_BASE_URL;
  if (u == null || String(u).trim() === '') return '/';
  return String(u).replace(/\/$/, '') || '/';
}

const instance: AxiosInstance = axios.create({
  baseURL: apiBaseURL(),
  timeout: 30_000,
  // Required for `Set-Cookie` on GET /api/admin/auth/csrf-token to be stored and
  // sent on POST /api/admin/auth/login (same-site admin + /api proxy).
  withCredentials: true,
});

instance.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

instance.interceptors.response.use(
  (res) => {
    const body = res.data;
    if (body && typeof body === 'object' && 'code' in body) {
      if (body.code === 0) {
        return { ...res, data: body.data };
      }
      throw new ApiError(body.code, body.msg ?? 'api error', body.fields, body.error_key);
    }
    return res;
  },
  (err: AxiosError<{ code?: number; msg?: string; fields?: Record<string, string>; error_key?: string }>) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    const body = err.response?.data;
    if (body && typeof body === 'object' && 'code' in body) {
      throw new ApiError(
        body.code ?? err.response!.status,
        body.msg ?? err.message,
        body.fields,
        body.error_key,
      );
    }
    throw new ApiError(err.response?.status ?? 0, err.message);
  },
);

export const api = {
  get: <T = unknown>(
    url: string,
    params?: unknown,
    config?: Omit<AxiosRequestConfig, 'params' | 'url' | 'method'>,
  ) => instance.get<T>(url, { params, ...config }).then((r) => r.data as unknown as T),
  post: <T = unknown>(
    url: string,
    body?: unknown,
    config?: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'>,
  ) => instance.post<T>(url, body, config).then((r) => r.data as unknown as T),
  patch: <T = unknown>(url: string, body?: unknown) =>
    instance.patch<T>(url, body).then((r) => r.data as unknown as T),
  put: <T = unknown>(url: string, body?: unknown) =>
    instance.put<T>(url, body).then((r) => r.data as unknown as T),
  del: <T = unknown>(url: string) => instance.delete<T>(url).then((r) => r.data as unknown as T),
};
